// File: src/components/ServiceForm.jsx
import React, { useState, useEffect } from "react";
import { TextField, Button, Container, Box, Typography, Divider } from "@mui/material";
import { createService, getServiceById, updateService } from "../services/serviceService";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import MediaUploadField from "./MediaUploadField";

function ServiceForm() {
  const [initialValues, setInitialValues] = useState({
    id: null,
    name: "",
    nameAr: "",
    description: "",
    descriptionAr: "",
    imageUrl: "",
    metaTitle: "",
    metaTitleAr: "",
    metaDescription: "",
    metaDescriptionAr: "",
    keywords: "",
    slug: "",
    content: "",
    contentAr: "",
  });

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchService();
    }
  }, [id]);

  const fetchService = async () => {
    try {
      const data = await getServiceById(id);
      setInitialValues({
        id: data.id,
        name: data.name,
        nameAr: data.nameAr || "",
        description: data.description,
        descriptionAr: data.descriptionAr || "",
        imageUrl: data.imageUrl,
        metaTitle: data.metaTitle,
        metaTitleAr: data.metaTitleAr || "",
        metaDescription: data.metaDescription,
        metaDescriptionAr: data.metaDescriptionAr || "",
        keywords: data.keywords,
        slug: data.slug,
        content: data.content,
        contentAr: data.contentAr || "",
      });
    } catch (error) {
      console.error("Error fetching service:", error);
    }
  };

  const ServiceSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    slug: Yup.string().required("Slug is required"),
    metaTitle: Yup.string(),
    metaDescription: Yup.string(),
    keywords: Yup.string(),
    description: Yup.string().required("Description is required"),
    content: Yup.string().required("Content is required"),
    // Arabic fields are optional
    nameAr: Yup.string(),
    descriptionAr: Yup.string(),
    metaTitleAr: Yup.string(),
    metaDescriptionAr: Yup.string(),
    contentAr: Yup.string(),
  });

  const handleSubmit = async (values) => {
    try {
      if (id) {
        await updateService(id, values);
      } else {
        await createService(values);
      }
      navigate("/services");
    } catch (error) {
      console.error("Error saving service:", error);
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="md">
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            {id ? "Edit Service" : "Create Service"}
          </Typography>
          
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={ServiceSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form>
                {/* English Fields Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                  English Content
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  label="Name (English)"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />

                <TextField
                  label="Slug"
                  name="slug"
                  value={values.slug}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.slug && Boolean(errors.slug)}
                  helperText={touched.slug && errors.slug}
                />

                {/* Rich Text Editor for Description */}
                <Box mt={2} mb={2}>
                  <label className="block text-sm font-medium text-gray-700">
                    Description (English) *
                  </label>
                  <ReactQuill
                    value={values.description}
                    onChange={(content) => setFieldValue("description", content)}
                    theme="snow"
                    placeholder="Enter service description..."
                  />
                  {touched.description && errors.description && (
                    <p style={{ color: "red", marginTop: "8px" }}>{errors.description}</p>
                  )}
                </Box>

                {/* Rich Text Editor for Content */}
                <Box mt={2} mb={2}>
                  <label className="block text-sm font-medium text-gray-700">
                    Content (English) *
                  </label>
                  <ReactQuill
                    value={values.content}
                    onChange={(content) => setFieldValue("content", content)}
                    theme="snow"
                    placeholder="Enter additional content..."
                  />
                  {touched.content && errors.content && (
                    <p style={{ color: "red", marginTop: "8px" }}>{errors.content}</p>
                  )}
                </Box>

                <TextField
                  label="Meta Title (English)"
                  name="metaTitle"
                  value={values.metaTitle}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                />

                <TextField
                  label="Meta Description (English)"
                  name="metaDescription"
                  value={values.metaDescription}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  multiline
                  rows={2}
                />

                {/* Arabic Fields Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'secondary.main' }}>
                  Arabic Content (اختياري)
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  label="Name (Arabic) - الاسم"
                  name="nameAr"
                  value={values.nameAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  inputProps={{ dir: 'rtl' }}
                  error={touched.nameAr && Boolean(errors.nameAr)}
                  helperText={touched.nameAr && errors.nameAr}
                />

                {/* Rich Text Editor for Arabic Description */}
                <Box mt={2} mb={2}>
                  <label className="block text-sm font-medium text-gray-700">
                    Description (Arabic) - الوصف
                  </label>
                  <ReactQuill
                    value={values.descriptionAr}
                    onChange={(content) => setFieldValue("descriptionAr", content)}
                    theme="snow"
                    placeholder="أدخل وصف الخدمة..."
                  />
                  {touched.descriptionAr && errors.descriptionAr && (
                    <p style={{ color: "red", marginTop: "8px" }}>{errors.descriptionAr}</p>
                  )}
                </Box>

                {/* Rich Text Editor for Arabic Content */}
                <Box mt={2} mb={2}>
                  <label className="block text-sm font-medium text-gray-700">
                    Content (Arabic) - المحتوى
                  </label>
                  <ReactQuill
                    value={values.contentAr}
                    onChange={(content) => setFieldValue("contentAr", content)}
                    theme="snow"
                    placeholder="أدخل المحتوى الإضافي..."
                  />
                  {touched.contentAr && errors.contentAr && (
                    <p style={{ color: "red", marginTop: "8px" }}>{errors.contentAr}</p>
                  )}
                </Box>

                <TextField
                  label="Meta Title (Arabic) - العنوان التعريفي"
                  name="metaTitleAr"
                  value={values.metaTitleAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  inputProps={{ dir: 'rtl' }}
                />

                <TextField
                  label="Meta Description (Arabic) - الوصف التعريفي"
                  name="metaDescriptionAr"
                  value={values.metaDescriptionAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  multiline
                  rows={2}
                  inputProps={{ dir: 'rtl' }}
                />

                {/* Common Fields Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'text.secondary' }}>
                  Additional Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <MediaUploadField
                  label="Service Image"
                  value={values.imageUrl}
                  onChange={(nextValue) => setFieldValue("imageUrl", nextValue)}
                  kind="image"
                  scope="services"
                  slug={values.slug}
                  error={errors.imageUrl}
                  touched={touched.imageUrl}
                />

                <TextField
                  label="Keywords (comma separated)"
                  name="keywords"
                  value={values.keywords}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                />

                <Box mt={4}>
                  <Button type="submit" variant="contained" color="primary" size="large" fullWidth>
                    {id ? "Update Service" : "Create Service"}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default ServiceForm;
